import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ModalBody, Spinner } from 'reactstrap';
import Modal from 'react-modal';
import { AiOutlineCheck, AiOutlineClose } from 'react-icons/ai';
import { DefaultButton } from '../../components/Button/Button';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { isMobile } from 'react-device-detect';
import { saveVehicle, getExistedPlate } from '../../core/utils/vehicles';
import { alertError, alertSuccess } from '../../components/Toast';
import {
  LabelInput,
  LicensePlateInput,
  LabelCreatable,
  ChassiInput,
} from '../../components/Input/Input';
import { BsCardText, BsPerson } from 'react-icons/bs';
import useVehicleForm from '../../core/hooks/useVehicleForm';
import { isValidPlate } from '../../core/utils/vehiclePlate';
import { supabase } from '../../supabaseClient';

const BudgetAddVehicle = ({
  setOpen,
  isOpen,
  setReload,
  defaultValues,
  currentVehicleId,
}) => {
  const company = useSelector((state) => state.app.company || {});
  const [isLoadingVehicle, setIsLoadingVehicle] = useState(false);

  const {
    values,
    setValues,
    loadingPlate,
    brandOptions,
    modelOptions,
    onChange: hookOnChange,
    createOption: hookCreateOption,
    changePlate: hookChangePlate,
    loadBrands,
  } = useVehicleForm();

  const onChange = (field, value) => hookOnChange({ field, value });
  const createOption = (name, field) => hookCreateOption({ name, field });
  const changePlate = (plate) => hookChangePlate({ plate });

  const containPlate = isValidPlate(values?.plate);

  const loadVehicleData = async () => {
    setIsLoadingVehicle(true);

    let { data, error } = await supabase
      .from('Vehicle')
      .select('*')
      .eq('id', currentVehicleId)
      .is('deleted_at', null);

    if (!error) {
      const item = data[0];
      setValues(item);
    }

    setIsLoadingVehicle(false);
  };

  useEffect(() => {
    if (currentVehicleId !== 0) {
      loadVehicleData();
    } else {
      setValues({});
    }
  }, [currentVehicleId, isOpen]);

  useEffect(() => {
    loadBrands();
  }, []);

  const closeModal = () => {
    setValues({});
    setOpen((open) => !open);
  };

  const onSave = async () => {
    const existedPlate = await getExistedPlate({
      plate: values.plate,
      companyId: company.id,
      excludeVehicleId: values.id,
    });
    try {
      const extraData = {};
      if (values && !values.brand) {
        alertError('Marca do veículo é obrigatória.');
        return;
      } else {
        if (values?.brand?.label) {
          extraData['brand'] = values?.brand?.label;
        }
      }
      if (values && !values.model) {
        alertError('Modelo do veículo é obrigatório.');
        return;
      } else {
        if (values?.model?.label) {
          extraData['model'] = values?.model?.label;
        }
      }
      if (values && !values.plate) {
        alertError('Placa do veículo é obrigatória.');
        return;
      }

      if (currentVehicleId === 0) {
        if (existedPlate?.length > 0) {
          alertError('Placa já cadastrada.');
          return;
        }
        await saveVehicle(undefined, {
          responsible: defaultValues?.client?.value,
          company: company.id,
          ...values,
          ...extraData,
        });
      } else {
        await saveVehicle(values.id, {
          responsible: defaultValues?.client?.value,
          company: company.id,
          ...values,
          ...extraData,
        });
      }
      alertSuccess('Sucesso ao salvar o veículo!');
      setOpen((open) => !open);
      setReload((reload) => !reload);
    } catch (error) {
      alertError('Erro ao salvar o veículo, tente novamente mais tarde.');
    }
  };

  return (
    <Modal
      onRequestClose={closeModal}
      className={'prospera-modal'}
      isOpen={isOpen}
    >
      <input style={{ visibility: 'hidden', position: 'absolute' }} />
      <ModalBody className="modal-body">
        <div style={{ position: 'relative' }} className="modal-container">
          {isLoadingVehicle && (
            <div
              style={{
                width: '100%',
                height: '100vh',
                position: 'absolute',
                opacity: 0.5,
                backgroundColor: 'black',
                zIndex: 100,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <div className="spinner-clear">
                <Spinner color="primary" size="lg" />
              </div>
            </div>
          )}
          <div className="modal-header">
            <div className="header-title">
              <h4 className="modal-title">
                {currentVehicleId ? 'Editar Veículo' : 'Adicionar Veículo'}
              </h4>
            </div>
            <div className="header-buttons">
              <DefaultButton
                className="modal-footer-button"
                label={isMobile ? '' : 'Salvar'}
                icon={<AiOutlineCheck className="button-icon" />}
                onClick={onSave}
              />
              <DefaultButton
                className="modal-footer-button cancel"
                label={isMobile ? '' : 'Cancelar'}
                icon={<AiOutlineClose className="button-icon-cancel" />}
                onClick={closeModal}
              />
            </div>
          </div>
          <Tabs>
            <TabList>
              <Tab>
                <text> Dados </text>
                <BsPerson />
              </Tab>
              <Tab>
                <text> Observações </text>
                <BsCardText />
              </Tab>
            </TabList>
            <TabPanel>
              {isMobile ? (
                <div>
                  <div className="">
                    <LicensePlateInput
                      loadingPlate={loadingPlate}
                      value={values.plate}
                      onChange={(value) => changePlate(value)}
                      placeholder={'Insira a placa do veículo'}
                      label={'Placa'}
                    />
                  </div>
                  <div className="">
                    <LabelInput
                      value={values.color}
                      onChange={(value) => onChange('color', value)}
                      placeholder={'Insira a cor do veículo'}
                      label={'Cor do veículo'}
                    />
                    <LabelInput
                      value={values.fuel}
                      onChange={(value) => onChange('fuel', value)}
                      placeholder={'Insira o combustível do veículo'}
                      label={'Combustível'}
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <div className="main-upper-div width-correction">
                    <LicensePlateInput
                      loadingPlate={loadingPlate}
                      value={values?.plate}
                      onChange={(value) => changePlate(value)}
                      placeholder={'Insira a placa do veículo'}
                      label={'Placa'}
                    />
                  </div>
                  <div className="main-upper-div width-correction">
                    <LabelInput
                      value={values?.color}
                      onChange={(value) => onChange('color', value)}
                      placeholder={'Insira a cor do veículo'}
                      label={'Cor do veículo'}
                    />
                    <LabelInput
                      value={values?.fuel}
                      onChange={(value) => onChange('fuel', value)}
                      placeholder={'Insira o combustível do veículo'}
                      label={'Combustível'}
                    />
                  </div>
                </div>
              )}
              <LabelCreatable
                onCreateOption={(name) => createOption(name, 'brand')}
                options={brandOptions?.map((item) => ({
                  value: item.codigo,
                  label: item.nome,
                }))}
                className="generic-input select-creatable"
                value={
                  values?.brand
                    ? [
                        brandOptions?.find(
                          (item) =>
                            item.nome === values?.brand ||
                            (item.nome &&
                              typeof item.nome === 'string' &&
                              item.nome.includes(values?.brand)) ||
                            (values?.brand.includes &&
                              typeof values?.brand.includes === 'function' &&
                              values?.brand.includes(item.nome))
                        ) || { label: values?.brand, value: null },
                      ]
                    : values?.brand
                }
                defaultValue={values?.brand}
                onChange={(value) => onChange('brand', value)}
                placeholder={'INSIRA A MARCA DO VEÍCULO'}
                label={'Marca'}
                disabled={!containPlate}
              />
              <LabelCreatable
                disabled={!values?.brand}
                onCreateOption={(name) => createOption(name, 'model')}
                options={modelOptions?.map((item) => ({
                  value: item.codigo,
                  label: item.nome,
                }))}
                className="generic-input select-creatable"
                value={
                  values?.model
                    ? [
                        modelOptions?.find(
                          (item) =>
                            item.nome === values?.model ||
                            (item.nome &&
                              typeof item.nome === 'string' &&
                              item.nome.includes(values?.model)) ||
                            (values?.model.includes &&
                              typeof values?.model.includes === 'function' &&
                              values?.model.includes(item.nome))
                        ) || { label: values?.model, value: null },
                      ]
                    : values?.model
                }
                defaultValue={values?.model}
                onChange={(value) => onChange('model', value)}
                placeholder={'INSIRA O MODELO DO VEÍCULO'}
                label={'Modelo'}
              />
              <LabelInput
                value={values?.year}
                onChange={(value) => onChange('year', value)}
                placeholder={'Insira o ano do veículo'}
                label={'Ano de fabricação'}
              />
              <LabelInput
                value={values?.yearmodel}
                onChange={(value) => onChange('yearmodel', value)}
                placeholder={'Insira o ano do modelo do veículo'}
                label={'Ano do modelo'}
              />
              <div>
                <ChassiInput
                  value={values?.chassi}
                  onChange={(value) => onChange('chassi', value)}
                  placeholder={'Insira o Chassi'}
                  label={'Chassi do veículo'}
                />
                <LabelInput
                  value={values?.engine}
                  onChange={(value) => onChange('engine', value)}
                  placeholder={'Insira o motor'}
                  label={'Motor do veículo'}
                />
              </div>
            </TabPanel>

            <TabPanel>
              <LabelInput
                type={'textarea'}
                value={values?.observation}
                onChange={(value) => onChange('observation', value)}
                placeholder={'Insira a observação'}
                inputClassname={'text-area-min'}
                label={'Observação'}
              />
            </TabPanel>
          </Tabs>

          <div className="modal-footer-table"></div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default BudgetAddVehicle;
